var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var __rest = this && this.__rest || function (s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};

import * as PropTypes from 'prop-types';
import * as React from 'react';
import { executeMediaQuery, Media } from './Media';
import { useDebugValue, useEffect, useMemo, useState } from 'react';
import { getMaxMatch, inBetween, nothingSet, notNulls, pickMatchValues, pickMediaMatch, pickMediaMatchSlot } from './utils';

var castPointsTo = function castPointsTo(points, targetType) {
  return Object.keys(points).reduce(function (acc, key) {
    acc[key] = targetType;
    return acc;
  }, {});
};

var skipProp = {}; // @ts-ignore

var use = function use() {
  var args = [];

  for (var _i = 0; _i < arguments.length; _i++) {
    args[_i] = arguments[_i];
  }

  return null;
};

export function createMediaMatcher(queries, firstKey) {
  use(firstKey);
  var mediaDefaults = executeMediaQuery(queries);
  var initialValue = {};
  var MediaContext = React.createContext(initialValue);

  var getMediaMatches = function getMediaMatches(matches) {
    return matches === initialValue ? mediaDefaults : matches;
  };

  var consume = function consume(cb) {
    return React.createElement(MediaContext.Consumer, null, function (matched) {
      return cb(getMediaMatches(matched));
    });
  };

  function pickMatch(matches, slots, defaultValue) {
    return pickMediaMatch(queries, matches, slots, defaultValue);
  }

  function useMedia(slots, defaultValue) {
    var matches = getMediaMatches(React.useContext(MediaContext));

    if (process.env.NODE_ENV !== 'production') {
      useDebugValue(pickMediaMatchSlot(queries, matches, slots) || 'default');
    }

    return pickMatch(matches, slots, defaultValue);
  }

  function pickMatchEx(matches, slots) {
    return pickMediaMatch(queries, matches, slots);
  }

  var ProvideMediaMatchers = function ProvideMediaMatchers(_a) {
    var children = _a.children,
        _b = _a.state,
        state = _b === void 0 ? null : _b;
    return React.createElement(MediaContext.Consumer, null, function (parentMatch) {
      return React.createElement(Media, {
        queries: queries
      }, function (matches) {
        var value = state || __assign({}, parentMatch || {}, notNulls(matches));

        return React.createElement(MediaContext.Provider, {
          value: value,
          children: children
        });
      });
    });
  };

  var MediaMatches = function MediaMatches(_a) {
    var children = _a.children;
    return consume(function (matched) {
      return children(matched, function (matches) {
        return pickMatch(matched, matches);
      });
    });
  };

  var InlineMediaMatcher = function InlineMediaMatcher(props) {
    return consume(function (matched) {
      return pickMatchEx(matched, props);
    });
  };

  var MediaMatcher = function MediaMatcher(props) {
    return consume(function (matched) {
      return pickMatchEx(matched, props);
    });
  };

  MediaMatcher.propTypes = // @ts-ignore
  process.env.NODE_ENV !== 'production' ? __assign({}, castPointsTo(queries, PropTypes.node)) : skipProp;

  var Mock = function Mock(_a) {
    var children = _a.children,
        props = __rest(_a, ["children"]);

    return React.createElement(MediaContext.Provider, {
      value: pickMatchValues(queries, __assign({}, nothingSet(queries), props))
    }, children);
  };

  var Override = function Override(_a) {
    var children = _a.children,
        props = __rest(_a, ["children"]);

    return React.createElement(MediaContext.Consumer, null, function (parentMatch) {
      var value = __assign({}, notNulls(parentMatch), pickMatchValues(queries, props));

      return React.createElement(MediaContext.Provider, {
        value: value,
        children: children
      });
    });
  };

  var Below = function Below(_a) {
    var children = _a.children,
        including = _a.including,
        props = __rest(_a, ["children", "including"]);

    return React.createElement(MediaMatcher, __assign({}, inBetween(queries, props, children, including ? false : true, true)));
  };

  var Above = function Above(_a) {
    var children = _a.children,
        including = _a.including,
        props = __rest(_a, ["children", "including"]);

    return React.createElement(MediaMatcher, __assign({}, inBetween(queries, props, children, including ? true : false, false)));
  };

  var MediaServerSide = function MediaServerSide(_a) {
    var predicted = _a.predicted,
        hydrated = _a.hydrated,
        onWrongPrediction = _a.onWrongPrediction,
        children = _a.children;

    var _b = useState(hydrated === undefined ? false : hydrated),
        isHydrated = _b[0],
        setHydrated = _b[1];

    var media = React.useContext(MediaContext);
    useEffect(function () {
      setHydrated(hydrated === undefined ? true : hydrated);
    }, [hydrated]);
    useEffect(function () {
      if (isHydrated && !media[predicted]) {
        if (onWrongPrediction) {
          onWrongPrediction(predicted, getMaxMatch(queries, media));
        }
      }
    }, [isHydrated]);
    var overrides = useMemo(function () {
      var _a;

      if (isHydrated) {
        return media;
      } else {
        return __assign({}, nothingSet(queries), (_a = {}, _a[predicted] = true, _a));
      }
    }, [media, isHydrated]);
    return React.createElement(MediaContext.Provider, {
      value: overrides,
      children: children
    });
  };

  var ServerRender = function ServerRender(_a) {
    var predicted = _a.predicted,
        hydrated = _a.hydrated,
        onWrongPrediction = _a.onWrongPrediction,
        children = _a.children;
    return React.createElement(ProvideMediaMatchers, null, React.createElement(MediaServerSide, {
      predicted: predicted,
      hydrated: hydrated,
      onWrongPrediction: onWrongPrediction,
      children: children
    }));
  };

  return {
    queries: queries,
    pickMatch: pickMatch,
    useMedia: useMedia,
    Provider: ProvideMediaMatchers,
    Mock: Mock,
    Override: Override,
    //
    Matches: MediaMatches,
    Inline: InlineMediaMatcher,
    Above: Above,
    Below: Below,
    //
    Matcher: MediaMatcher,
    ServerRender: ServerRender,
    //
    Gearbox: MediaContext.Consumer,
    Consumer: MediaContext.Consumer
  };
}